import AOS from "aos";
import { useEffect } from "react";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import HomePage from "./pages/index";
import AboutPage from "./pages/about";
import PricingPage from "./pages/pricing";
import ContactPage from "./pages/contact";
import ProjectPage from "./pages/project";
import ProjectDetails from "./templates/project-details";
import BlogDetails from "./templates/blog-details";
import BlogCategory from "./templates/blog-category";
import BlogDate from "./templates/blog-date";
import BlogAuthor from "./templates/blog-author";
import BlogTag from "./templates/blog-tag";
import BlogPage from "./pages/blog";
import NotFoundPage from "./pages/404";
import "./assets/css/plugins/fontawesome.min.css";
import "./assets/css/plugins/icofont.min.css";
import "aos/dist/aos.css";
import "./assets/css/plugins/animate.css";
import "swiper/components/navigation/navigation.scss";
import "swiper/swiper.scss";
import "./assets/scss/style.scss";
import NavScrollTop from "./components/nav-scroll-top";
import { trackVisitor } from "./components/TrackerService";
import AdminDashboard from "./pages/admin";
import { getNextVisitorId } from "./services/counterService";
import Plausible from "plausible-tracker";
import { clarity } from "react-microsoft-clarity";

const App = () => {
    useEffect(() => {
        const { enableAutoPageviews } = Plausible({
            domain: "sajid.ami.bd",
        });
        enableAutoPageviews();

        clarity.init("v81sfynhyd");

        AOS.init({
            offset: 80,
            duration: 1000,
            once: true,
            easing: "ease",
        });
        AOS.refresh();

        const captureVisitorData = async () => {
            try {
                // Fetch IP geolocation data
                console.log("Fetching visitor IP data...");
                let ipData = null;
                try {
                    const response = await fetch(
                        "https://api.ipgeolocation.io/ipgeo?apiKey=8cc6e813cbe34e5599a0ac7178cc19e2"
                    );
                    if (!response.ok) {
                        throw new Error(
                            `HTTP error! Status: ${response.status}`
                        );
                    }
                    ipData = await response.json();
                } catch (err) {
                    throw new Error(`IP Geolocation failed: ${err.message}`);
                }

                // Ensure ipData is valid before proceeding
                if (!ipData || !ipData.ip) {
                    console.error(
                        "Invalid IP data received, skipping visitor tracking."
                    );
                    return;
                }

                // Function to get browser's GPS location
                const getGPSLocation = () => {
                    return new Promise((resolve, reject) => {
                        if (!navigator.geolocation) {
                            return reject("Geolocation not supported");
                        }

                        navigator.geolocation.getCurrentPosition(
                            (position) => {
                                resolve({
                                    latitude: position.coords.latitude,
                                    longitude: position.coords.longitude,
                                    source: "GPS",
                                });
                            },
                            (error) => {
                                console.warn(
                                    "Geolocation denied or failed:",
                                    error
                                );
                                reject(error);
                            },
                            { timeout: 10000, enableHighAccuracy: true }
                        );
                    });
                };

                let location;
                try {
                    location = await getGPSLocation();
                } catch (error) {
                    console.log("Falling back to IP-based location.");
                    location = {
                        latitude: ipData.latitude || null,
                        longitude: ipData.longitude || null,
                        source: "IP",
                    };
                }

                const visitorId = await getNextVisitorId();

                // Ensure valid values, fallback if undefined
                const visitorData = {
                    id: visitorId,
                    timestamp: new Date().toISOString(),
                    ip: ipData.ip || "Unknown",
                    continent: ipData.continent_name || "Unknown",
                    country: {
                        name: ipData.country_name || "Unknown",
                        code: ipData.country_code2 || "Unknown",
                    },
                    state: {
                        name:
                            ipData.state_prov || ipData.state_name || "Unknown",
                        code: ipData.state_code || "Unknown",
                    },
                    city: ipData.city || "Unknown",
                    latitude: location.latitude,
                    longitude: location.longitude,
                    location_source: location.source,
                    isp: ipData.isp || "Unknown",
                    time_zone: ipData.time_zone?.name || "Unknown",
                    browser: navigator.userAgent,
                    raw_ip_data: ipData, // Store the full API response
                };

                await trackVisitor(visitorData);
                // console.log("Visitor data saved:", visitorData);

                // Store in localStorage to prevent repeat logs today
                localStorage.setItem("last_visitor_ip", ipData.ip);
                localStorage.setItem(
                    "last_visitor_date",
                    new Date().toDateString()
                );
            } catch (error) {
                // console.error("Error capturing visitor data:", error);
            }
        };

        const checkDeduplication = () => {
            const lastIp = localStorage.getItem("last_visitor_ip");
            const lastDate = localStorage.getItem("last_visitor_date");
            const today = new Date().toDateString();

            // Allow bypass via query param for testing
            const urlParams = new URLSearchParams(window.location.search);
            if (urlParams.get("force") === "true") return false;

            if (lastIp && lastDate === today) {
                // console.log("Visitor already logged today. Skipping tracking.");
                return true;
            }
            return false;
        };

        // Call captureVisitorData immediately after defining it if not already logged today
        if (!checkDeduplication()) {
            captureVisitorData();
        }
    }, []); // Empty dependency array ensures this runs only once when the component is mounted

    return (
        <Router>
            <NavScrollTop>
                <Switch>
                    <Route path="/admin" component={AdminDashboard} />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/"}`}
                        exact
                        component={HomePage}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/about"}`}
                        component={AboutPage}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/pricing"}`}
                        component={PricingPage}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/project"}`}
                        component={ProjectPage}
                    />
                    <Route
                        path={`${
                            process.env.PUBLIC_URL + "/project-detalis/:id"
                        }`}
                        component={ProjectDetails}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/blog"}`}
                        component={BlogPage}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/blog-details/:id"}`}
                        component={BlogDetails}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/category/:slug"}`}
                        component={BlogCategory}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/date/:date"}`}
                        component={BlogDate}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/author/:author"}`}
                        component={BlogAuthor}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/tag/:slug"}`}
                        component={BlogTag}
                    />
                    <Route
                        path={`${process.env.PUBLIC_URL + "/contact"}`}
                        component={ContactPage}
                    />
                    <Route component={NotFoundPage} />
                </Switch>
            </NavScrollTop>
        </Router>
    );
};

export default App;
